/**
 * useLiveStream — reusable Live Stream TTS orchestrator hook.
 *
 * Wraps the browser-side Web Audio + SSE plumbing that powers the
 * "audio plays as it's generated" experience shipped in v5.10.0.
 * The hook lets any page (TTS, Universal Translator, future pages)
 * consume the same streaming pipeline without inheriting all of the
 * TTSLayoutEnhanced state.
 *
 * Design:
 *   - The hook OWNS the PcmStreamer + EventSource lifecycle and the
 *     `isStreamingLive` / `streamingJobId` UI state. Callers just
 *     invoke `prime()`, `openStream(jobData)`, `streamBatch(...)`,
 *     `stop()` and read the state.
 *   - Callers inject page-specific side effects via callbacks
 *     (`onFirstChunk`, `onPreviewOnly`, `onError`, `onDone`) so the
 *     hook stays free of TTS-layout state names.
 *   - The hook handles Safari autoplay: `prime()` creates + resumes
 *     the AudioContext inside the user gesture; subsequent chunks
 *     schedule onto that already-running context.
 *
 * NOTE: TTSLayoutEnhanced.js still contains its own copy of this
 * logic. Migrating it to consume this hook is a follow-up refactor
 * kept out of the S2S MVP to avoid regression risk on the TTS page.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { PcmStreamer } from '../utils/geminiPcmStreamer'

export function useLiveStream(options = {}) {
  const {
    // Called once when the first PCM chunk arrives — good spot to
    // clear a "Processing..." spinner.
    onFirstChunk = null,
    // Called on `preview_only` SSE frame (Custom Voice + streaming).
    // Rare and probably filtered upstream, but if it happens the
    // caller may want to surface a banner.
    onPreviewOnly = null,
    // Called with the `done` SSE payload after a stream completes.
    onDone = null,
    // Called with Error when the stream errors / is cancelled / SSE
    // fails. Hook already tore down state; caller can log or show a
    // banner. (openStream also rejects with the same Error so callers
    // that prefer Promise-style handling can await + catch.)
    onError = null,
    // Sample rate the server is emitting PCM at. Gemini streaming
    // is 24 kHz mono 16-bit today.
    sampleRate = 24000,
  } = options

  const pcmStreamerRef = useRef(null)
  const eventSourceRef = useRef(null)
  const abortBatchRef = useRef(false)
  const [isStreamingLive, setIsStreamingLive] = useState(false)
  const [streamingJobId, setStreamingJobId] = useState(null)

  // Create the streamer eagerly OR reuse the one already primed. Safe
  // to call multiple times — PcmStreamer's underlying _ensureContext
  // no-ops after the first call.
  const ensureStreamer = useCallback(() => {
    if (!pcmStreamerRef.current) {
      pcmStreamerRef.current = new PcmStreamer({
        sampleRate,
        onEnded: () => console.log('[useLiveStream] Playback finished'),
        onError: (e) => console.warn('[useLiveStream] PcmStreamer error:', e),
      })
    }
    return pcmStreamerRef.current
  }, [sampleRate])

  /**
   * Prime the AudioContext inside a user gesture so Safari's autoplay
   * policy is satisfied. Call this synchronously from the click handler
   * that triggers the submit — BEFORE any await. Idempotent.
   */
  const prime = useCallback(() => {
    const streamer = ensureStreamer()
    try { streamer.prime() } catch (e) {
      console.warn('[useLiveStream] prime failed:', e)
    }
  }, [ensureStreamer])

  /**
   * Open the SSE stream returned by the backend and wire PCM chunks
   * into the streamer. Returns a Promise that:
   *   - resolves with the `done` payload on success. The streamer is
   *     NOT ended so the caller can chain another phrase (batch mode).
   *   - rejects with an Error on server-side error, cancellation, or
   *     transport failure. The streamer is cancelled + UI state cleared.
   *
   * `jobData` is whatever the submit endpoint returned. Must include
   * `stream_url` to actually open a stream — otherwise resolves null
   * (i.e. "not a streaming job").
   */
  const openStream = useCallback((jobData) => {
    if (!jobData || !jobData.stream_url) return Promise.resolve(null)

    return new Promise((resolve, reject) => {
      if (eventSourceRef.current) {
        try { eventSourceRef.current.close() } catch (_) {/* no-op */}
      }
      setIsStreamingLive(true)
      setStreamingJobId(jobData.id)

      const streamer = ensureStreamer()
      let firstChunkSeen = false
      let settled = false  // guard against double-resolve from onerror after done

      const es = new EventSource(jobData.stream_url)
      eventSourceRef.current = es

      const closeSse = () => {
        try { es.close() } catch (_) {/* no-op */}
        if (eventSourceRef.current === es) eventSourceRef.current = null
      }

      const settleError = (err) => {
        if (settled) return
        settled = true
        closeSse()
        try { streamer.cancel() } catch (_) {/* no-op */}
        setIsStreamingLive(false)
        setStreamingJobId(null)
        if (onError) { try { onError(err) } catch (_) {/* swallow */} }
        reject(err)
      }

      es.onmessage = (ev) => {
        if (settled) return
        try {
          const msg = JSON.parse(ev.data)
          if (msg.type === 'chunk' && msg.pcm_b64) {
            streamer.appendChunk(msg.pcm_b64)
            if (!firstChunkSeen) {
              firstChunkSeen = true
              if (onFirstChunk) { try { onFirstChunk() } catch (e) { console.warn('[LiveStream] onFirstChunk callback threw:', e?.message || e) } }
            }
          } else if (msg.type === 'preview_only') {
            if (onPreviewOnly) { try { onPreviewOnly(msg) } catch (e) { console.warn('[LiveStream] onPreviewOnly callback threw:', e?.message || e) } }
          } else if (msg.type === 'done') {
            settled = true
            closeSse()
            if (onDone) { try { onDone(msg) } catch (e) { console.warn('[LiveStream] onDone callback threw:', e?.message || e) } }
            resolve(msg)
          } else if (msg.type === 'error') {
            settleError(new Error(`Stream error [${msg.code || 'unknown'}]: ${msg.message || ''}`))
          } else if (msg.type === 'cancelled') {
            settleError(new Error('Stream cancelled'))
          }
          // type 'open' is informational — nothing to do.
        } catch (parseErr) {
          console.warn('[useLiveStream] Bad SSE frame:', parseErr, ev.data)
        }
      }

      es.onerror = () => settleError(new Error('EventSource error'))
    })
  }, [ensureStreamer, onFirstChunk, onPreviewOnly, onDone, onError])

  /**
   * Batched streaming for texts longer than the per-request cap.
   * Submits phrases sequentially through `submitFn(phraseText, index)`
   * (caller-supplied — typically a wrapper around POST /v1/tts/jobs/
   * that adds the batch_id / batch_sequence / batch_total fields).
   *
   * Feeds each returned jobData into openStream and awaits its `done`
   * before moving to the next. The SAME PcmStreamer is reused across
   * phrases so playback is gapless (the playhead carries forward).
   *
   * After the last phrase (or after abort), calls streamer.end() so
   * the streamer's onEnded callback fires when the last buffer plays.
   *
   * Returns { delivered, aborted } — caller can persist / refund /
   * report accordingly.
   */
  const streamBatch = useCallback(async (phrases, submitFn) => {
    if (!Array.isArray(phrases) || phrases.length === 0) {
      return { delivered: 0, aborted: false }
    }
    abortBatchRef.current = false
    prime()  // ensure the AudioContext is primed once for the whole batch
    let delivered = 0
    let aborted = false
    try {
      for (let i = 0; i < phrases.length; i++) {
        if (abortBatchRef.current) { aborted = true; break }
        const jobData = await submitFn(phrases[i], i)
        if (!jobData) { aborted = true; break }
        try {
          await openStream(jobData)
          delivered += 1
        } catch (err) {
          // Stream failed / was cancelled — bail out of the batch loop.
          // openStream already cleared state via onError.
          aborted = true
          break
        }
      }
    } finally {
      const streamer = pcmStreamerRef.current
      if (streamer) { try { streamer.end() } catch (_) {} }
      setIsStreamingLive(false)
      setStreamingJobId(null)
    }
    return { delivered, aborted }
  }, [openStream, prime])

  /**
   * Stop the current stream / batch immediately.
   *   - abortBatchRef flips true so any in-flight batch loop breaks
   *     BEFORE submitting the next phrase.
   *   - EventSource closes → server sees disconnect → refunds the
   *     in-flight phrase proportionally.
   *   - PcmStreamer.cancel() truncates in-flight audio.
   */
  const stop = useCallback(() => {
    abortBatchRef.current = true
    if (eventSourceRef.current) {
      try { eventSourceRef.current.close() } catch (_) {}
      eventSourceRef.current = null
    }
    if (pcmStreamerRef.current) {
      try { pcmStreamerRef.current.cancel() } catch (_) {}
    }
    setIsStreamingLive(false)
    setStreamingJobId(null)
  }, [])

  // Cleanup on unmount so a page navigation while streaming doesn't
  // leave the SSE connection or AudioContext hanging.
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        try { eventSourceRef.current.close() } catch (_) {}
      }
      if (pcmStreamerRef.current) {
        try { pcmStreamerRef.current.cancel() } catch (_) {}
      }
    }
  }, [])

  return {
    prime,
    openStream,
    streamBatch,
    stop,
    isStreamingLive,
    streamingJobId,
    // Advanced escape hatch: expose refs so callers that need the
    // underlying PcmStreamer (e.g. to append raw chunks from a
    // non-SSE source) can reach it.
    pcmStreamerRef,
  }
}
